import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import {
    RefreshCw, AlertTriangle, Info, Rocket, TrendingUp, TrendingDown,
    Lightbulb, Layers, UserCheck,
} from 'lucide-react';
import { MetricCard } from '@/components/shared/MetricCard';
import {
    useEarlyLandingAnalytics,
    CATEGORY_LABELS,
    EXPERIENCE_LABELS,
    WATER_LABELS,
    activatorLabel,
    type EarlyLandingAnalytics,
    type ActivatorCatalogItem,
    type ExperienceSegment,
    type WaterSegment,
    type ModalitySegment,
} from '@/hooks/useEarlyLandingAnalytics';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { EarlyLandingPatternsPanel } from '@/components/analytics/EarlyLandingPatternsPanel';
import { OnboardingFunnelPanel } from '@/components/analytics/OnboardingFunnelPanel';
import { MainScreensPanel } from '@/components/analytics/MainScreensPanel';
import {
    DEFAULT_PROFILE_LENS,
    describeLens,
    isLensActive,
    type ProfileLens,
} from '@/components/analytics/profileLens';
import { LANDING_ANALYTICS_DAY_OPTIONS } from '@/components/analytics/periodOptions';

const COLORS = {
    ocean: 'hsl(199, 89%, 48%)',
    teal: 'hsl(172, 66%, 50%)',
    amber: 'hsl(38, 92%, 50%)',
    emerald: 'hsl(142, 71%, 45%)',
    purple: 'hsl(262, 83%, 58%)',
    rose: 'hsl(346, 77%, 55%)',
};

const tooltipStyle = {
    background: 'hsl(222 47% 9%)',
    border: '1px solid hsl(222 30% 16%)',
    borderRadius: '8px',
    fontSize: '12px',
};

const HIGHLIGHT_KEYS = [
    'first_capture', 'catch_detail', 'follow', 'share', 'board',
    'rincon_nivel', 'rincon_comunidad', 'rincon_academia', 'rincon_tienda',
    'intel_hub', 'intel_chat', 'intel_reports', 'planificar',
    'academy_article', 'club_join', 'mission_claim', 'quiz', 'battle',
    'profile_done', 'search_users', 'user_profile', 'onboarding_v2',
];

export type LandingSection = 'overview' | 'actions' | 'patterns' | 'onboarding';

const LANDING_SECTIONS: LandingSection[] = ['overview', 'actions', 'patterns', 'onboarding'];

function isLandingSection(value: string | null | undefined): value is LandingSection {
    return !!value && (LANDING_SECTIONS as string[]).includes(value);
}

/** Labels amigables: el key onboarding_v2 es histórico en BD. */
const LABEL_OVERRIDES: Record<string, string> = {
    onboarding_v2: 'Onboarding completado',
};

function parseExperience(value: string | null | undefined): ProfileLens['experience'] {
    if (value && value in EXPERIENCE_LABELS) return value as ProfileLens['experience'];
    return 'all';
}

function parseWater(value: string | null | undefined): ProfileLens['water'] {
    if (value && value in WATER_LABELS) return value as ProfileLens['water'];
    return 'all';
}

interface EarlyLandingPanelProps {
    days: number;
    onDaysChange: (days: number) => void;
    /** Sección inicial (p. ej. desde ?section=patterns) */
    initialSection?: string | null;
    onSectionChange?: (section: LandingSection) => void;
    initialExperience?: string | null;
    initialWater?: string | null;
    initialModality?: string | null;
    onLensChange?: (lens: ProfileLens) => void;
}

export function EarlyLandingPanel({
    days,
    onDaysChange,
    initialSection,
    onSectionChange,
    initialExperience,
    initialWater,
    initialModality,
    onLensChange,
}: EarlyLandingPanelProps) {
    const [selectedLifeDay, setSelectedLifeDay] = useState<number>(1);
    const [section, setSection] = useState<LandingSection>(
        isLandingSection(initialSection) ? initialSection : 'overview'
    );
    const [actionCategory, setActionCategory] = useState<string>('all');
    const [onboardingDays, setOnboardingDays] = useState(days);
    const [lens, setLens] = useState<ProfileLens>(() => ({
        ...DEFAULT_PROFILE_LENS,
        experience: parseExperience(initialExperience),
        water: parseWater(initialWater),
        modality: initialModality?.trim() || 'all',
    }));

    const { data, isLoading, isError, error, refetch, isFetching } = useEarlyLandingAnalytics(days, {
        experience: lens.experience,
        water: lens.water,
        modality: lens.modality,
    });

    useEffect(() => {
        if (isLandingSection(initialSection)) {
            setSection(initialSection);
        }
    }, [initialSection]);

    useEffect(() => {
        setLens((prev) => ({
            ...prev,
            experience: parseExperience(initialExperience),
            water: parseWater(initialWater),
            modality: initialModality?.trim() || 'all',
        }));
    }, [initialExperience, initialWater, initialModality]);

    function selectSection(next: LandingSection) {
        setSection(next);
        onSectionChange?.(next);
    }

    function updateLens(patch: Partial<ProfileLens>) {
        setLens((prev) => {
            const next = { ...prev, ...patch };
            onLensChange?.(next);
            return next;
        });
    }

    function labelFor(key: string, catalog: ActivatorCatalogItem[] | undefined): string {
        return LABEL_OVERRIDES[key] ?? activatorLabel(key, catalog);
    }

    const categories = useMemo(() => {
        if (!data) return ['all'];
        const set = new Set(data.key_actions_48h.map((a) => a.category));
        return ['all', ...Array.from(set).sort()];
    }, [data]);

    if (isLoading && !data) {
        return (
            <div className="py-16 flex flex-col items-center justify-center gap-3">
                <RefreshCw size={24} className="animate-spin text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Cargando aterrizaje y patrones…</p>
                <p className="text-[11px] text-muted-foreground/80">Puede tardar ~30 s según el periodo.</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-6 text-center space-y-3">
                <p className="text-sm font-semibold text-rose-400">No se pudo cargar Aterrizaje</p>
                <p className="text-xs text-muted-foreground max-w-md mx-auto">
                    {(error as Error)?.message || 'Error al consultar get_early_landing_analytics'}
                </p>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-medium bg-primary text-primary-foreground"
                >
                    <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
                    Reintentar
                </button>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="py-16 text-center text-muted-foreground text-sm">
                No hay datos de aterrizaje para este periodo
            </div>
        );
    }

    const dayBehavior = data.day_behavior.find((d) => d.day === selectedLifeDay)
        ?? data.day_behavior[0];

    const sessionChart = data.session_funnel.map((s) => ({
        name: `S${s.session}`,
        users: s.users,
        pct: s.pct_of_registered,
    }));

    const dayChart = data.day_retention.map((d) => ({
        name: `D${d.day}`,
        pct: d.pct,
        active: d.active,
        eligible: d.eligible,
    }));

    const filteredActions = data.key_actions_48h.filter(
        (a) => actionCategory === 'all' || a.category === actionCategory
    );

    const sections: { value: LandingSection; label: string; icon: React.ReactNode }[] = [
        { value: 'overview', label: 'Primeras sesiones', icon: <Rocket size={14} /> },
        { value: 'onboarding', label: '0 · Onboarding', icon: <UserCheck size={14} /> },
        { value: 'actions', label: 'Acciones clave', icon: <Layers size={14} /> },
        { value: 'patterns', label: 'Patrones', icon: <Lightbulb size={14} /> },
    ];

    const expSeg: ExperienceSegment | undefined = data.patterns.by_experience_level.find(
        (s) => s.segment === lens.experience
    );
    const waterSeg: WaterSegment | undefined = data.patterns.by_water_type.find(
        (s) => s.segment === lens.water
    );
    const modalitySeg: ModalitySegment | undefined = data.patterns.by_modality.find(
        (s) => s.modality === lens.modality
    );
    const modalityOptions = data.patterns.by_modality
        .map((m) => m.modality)
        .filter((m) => m && m !== 'sin_dato');

    return (
        <div className="space-y-6 min-w-0 max-w-full">
            <div className="flex flex-wrap items-center justify-between gap-3 min-w-0">
                <div className="min-w-0">
                    <p className="text-sm text-muted-foreground">
                        Cohorte: {data.cohort.definition}. Día 1 = día de alta.
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        Historia: onboarding (cap. 0) → sesiones 1–3 → quién vuelve (S2/S5/D7) → playbooks por perfil.
                        La lente filtra la cohorte en servidor (embudo y retención del segmento).
                    </p>
                </div>
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <div className="flex flex-wrap gap-1.5 min-w-0">
                        {LANDING_ANALYTICS_DAY_OPTIONS.map((d) => (
                            <button
                                key={d}
                                type="button"
                                onClick={() => {
                                    onDaysChange(d);
                                    setOnboardingDays(d);
                                }}
                                className={cn(
                                    'px-3 sm:px-4 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all shrink-0',
                                    days === d
                                        ? 'bg-primary text-primary-foreground shadow-sm'
                                        : 'bg-muted/50 text-muted-foreground hover:text-foreground'
                                )}
                            >
                                {d} días
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => refetch()}
                        className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 shrink-0"
                        title="Actualizar"
                    >
                        <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
                    </button>
                </div>
            </div>

            {/* Lente de perfil — siempre visible */}
            <div className="rounded-xl border border-primary/25 bg-primary/5 p-3 sm:p-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <p className="text-sm font-semibold text-foreground">Analizando perfil</p>
                        <p className="text-xs text-muted-foreground">
                            {isLensActive(lens)
                                ? describeLens(lens)
                                : 'Todos los perfiles — elige un segmento para enfocar la lectura'}
                        </p>
                    </div>
                    {isLensActive(lens) && (
                        <button
                            type="button"
                            onClick={() => updateLens(DEFAULT_PROFILE_LENS)}
                            className="text-xs text-primary hover:underline"
                        >
                            Limpiar filtros
                        </button>
                    )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <label className="space-y-1">
                        <span className="text-[11px] text-muted-foreground">Experiencia</span>
                        <select
                            value={lens.experience}
                            onChange={(e) => updateLens({ experience: e.target.value as ProfileLens['experience'] })}
                            className="w-full bg-background border border-border rounded-md text-xs px-2 py-2 text-foreground"
                        >
                            <option value="all">Todos</option>
                            <option value="principiante">Principiante</option>
                            <option value="intermedio">Intermedio</option>
                            <option value="experto">Experto</option>
                            <option value="sin_dato">Sin dato</option>
                        </select>
                    </label>
                    <label className="space-y-1">
                        <span className="text-[11px] text-muted-foreground">Tipo de agua</span>
                        <select
                            value={lens.water}
                            onChange={(e) => updateLens({ water: e.target.value as ProfileLens['water'] })}
                            className="w-full bg-background border border-border rounded-md text-xs px-2 py-2 text-foreground"
                        >
                            <option value="all">Todos</option>
                            <option value="dulce">Agua dulce</option>
                            <option value="salada">Agua salada</option>
                            <option value="ambas">Ambas</option>
                            <option value="sin_dato">Sin dato</option>
                        </select>
                    </label>
                    <label className="space-y-1">
                        <span className="text-[11px] text-muted-foreground">Modalidad</span>
                        <select
                            value={lens.modality}
                            onChange={(e) => updateLens({ modality: e.target.value })}
                            className="w-full bg-background border border-border rounded-md text-xs px-2 py-2 text-foreground"
                        >
                            <option value="all">Todas</option>
                            {modalityOptions.map((m) => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>
                    </label>
                </div>

                {(expSeg || waterSeg || modalitySeg) && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
                        {expSeg && (
                            <ProfileFocusCard
                                title={EXPERIENCE_LABELS[expSeg.segment] || expSeg.segment}
                                users={expSeg.users}
                                s2={expSeg.session_2_pct}
                                s5={expSeg.session_5_pct}
                                d7={expSeg.day_7_pct}
                                extra={`Creators ${expSeg.creator_pct}% · Lurkers ${expSeg.lurker_pct}%`}
                            />
                        )}
                        {waterSeg && (
                            <ProfileFocusCard
                                title={WATER_LABELS[waterSeg.segment] || waterSeg.segment}
                                users={waterSeg.users}
                                s2={waterSeg.session_2_pct}
                                s5={waterSeg.session_5_pct}
                                d7={waterSeg.day_7_pct}
                                extra={`Creators ${waterSeg.creator_pct}%`}
                            />
                        )}
                        {modalitySeg && (
                            <ProfileFocusCard
                                title={modalitySeg.modality}
                                users={modalitySeg.users}
                                s2={null}
                                s5={modalitySeg.session_5_pct}
                                d7={modalitySeg.day_7_pct}
                                extra={`Creators ${modalitySeg.creator_pct}%`}
                            />
                        )}
                    </div>
                )}

                {!isLensActive(lens) && (
                    <div className="flex flex-wrap gap-1.5">
                        {data.patterns.by_experience_level
                            .filter((s) => ['principiante', 'intermedio', 'experto'].includes(s.segment))
                            .map((s) => (
                                <button
                                    key={s.segment}
                                    type="button"
                                    onClick={() => updateLens({ experience: s.segment as ProfileLens['experience'] })}
                                    className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-muted/60 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                >
                                    {EXPERIENCE_LABELS[s.segment]} · S5 {s.session_5_pct}%
                                </button>
                            ))}
                    </div>
                )}
            </div>

            <div className="flex gap-1 p-1 bg-muted/50 rounded-lg w-fit flex-wrap">
                {sections.map((s) => (
                    <button
                        key={s.value}
                        type="button"
                        onClick={() => selectSection(s.value)}
                        className={cn(
                            'flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all',
                            section === s.value
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        {s.icon}
                        {s.label}
                    </button>
                ))}
            </div>

            {/* Insights: ocultos en Patrones (van en playbooks). Filtra ruido sin_dato. */}
            {section !== 'patterns' && (() => {
                const insights = data.insights.filter(
                    (ins) => !/sin_dato/i.test(`${ins.title} ${ins.body}`)
                );
                if (insights.length === 0) return null;
                return (
                    <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-foreground">Conclusiones</h3>
                        {insights.map((insight, i) => (
                            <div
                                key={i}
                                className={cn(
                                    'flex gap-3 rounded-xl border p-4',
                                    insight.severity === 'warning'
                                        ? 'border-amber-500/30 bg-amber-500/5'
                                        : 'border-primary/30 bg-primary/5'
                                )}
                            >
                                {insight.severity === 'warning'
                                    ? <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                                    : <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />}
                                <div>
                                    <p className="text-sm font-semibold text-foreground">{insight.title}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">{insight.body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                );
            })()}

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <MetricCard title="Registrados" value={data.summary_kpis.registered} className="p-3" />
                <MetricCard title="Con sesión" value={data.summary_kpis.with_session} className="p-3" />
                <MetricCard title="% sesión 2" value={`${data.summary_kpis.session_2_pct}%`} className="p-3" />
                <MetricCard title="% sesión 5" value={`${data.summary_kpis.session_5_pct}%`} className="p-3" />
                <MetricCard title="% día 1" value={`${data.summary_kpis.day_1_pct}%`} className="p-3" />
                <MetricCard title="% día 7" value={`${data.summary_kpis.day_7_pct}%`} className="p-3" />
            </div>

            {section === 'patterns' && (
                <EarlyLandingPatternsPanel days={days} patterns={data.patterns} lens={lens} />
            )}

            {section === 'onboarding' && (
                <OnboardingFunnelPanel
                    days={onboardingDays}
                    onDaysChange={setOnboardingDays}
                    showPeriodSelector={false}
                />
            )}

            {section === 'actions' && (
                <div className="space-y-6">
                    {/* Profile steps — resumen; detalle opened→completed en pestaña Onboarding */}
                    <div className="rounded-xl border border-border bg-card p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                            <h3 className="text-sm font-semibold text-foreground">Pasos del perfil (en cohorte)</h3>
                            <button
                                type="button"
                                onClick={() => selectSection('onboarding')}
                                className="text-xs text-primary hover:underline"
                            >
                                Ver embudo completo →
                            </button>
                        </div>
                        <p className="text-xs text-muted-foreground mb-3">
                            % de registrados que completaron cada paso (primeras sesiones)
                        </p>
                        <div className="space-y-2">
                            {data.profile_steps.map((step) => (
                                <div key={step.step} className="space-y-1">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-foreground font-medium">{step.label}</span>
                                        <span className="text-muted-foreground">
                                            {step.completed.toLocaleString()} · {step.pct}%
                                        </span>
                                    </div>
                                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                                        <div
                                            className="h-full rounded-full bg-primary"
                                            style={{ width: `${Math.min(100, Math.max(2, step.pct))}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Key actions 48h */}
                    <div className="rounded-xl border border-border bg-card p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                            <div>
                                <h3 className="text-sm font-semibold text-foreground">Acciones clave (48 h)</h3>
                                <p className="text-xs text-muted-foreground">
                                    % de registrados que realizaron cada acción en las primeras 48 horas
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {categories.map((cat) => (
                                    <button
                                        key={cat}
                                        type="button"
                                        onClick={() => setActionCategory(cat)}
                                        className={cn(
                                            'px-2.5 py-1 rounded-md text-xs font-medium',
                                            actionCategory === cat
                                                ? 'bg-primary text-primary-foreground'
                                                : 'bg-muted/50 text-muted-foreground hover:text-foreground'
                                        )}
                                    >
                                        {cat === 'all' ? 'Todas' : (CATEGORY_LABELS[cat] || cat)}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {filteredActions.map((a) => (
                                <div
                                    key={a.key}
                                    className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2 flex items-center justify-between gap-2"
                                >
                                    <div className="min-w-0">
                                        <p className="text-[11px] text-muted-foreground truncate">
                                            {CATEGORY_LABELS[a.category] || a.category}
                                        </p>
                                        <p className="text-sm font-medium text-foreground truncate">
                                            {LABEL_OVERRIDES[a.key] ?? a.label}
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="text-sm font-bold text-foreground">{a.pct}%</p>
                                        <p className="text-[10px] text-muted-foreground">{a.users} users</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Activation by session — highlight keys */}
                    <div className="rounded-xl border border-border bg-card p-4 overflow-x-auto">
                        <h3 className="text-sm font-semibold text-foreground mb-1">Activación acumulada por sesión</h3>
                        <p className="text-xs text-muted-foreground mb-3">
                            Entre quienes llegaron a esa sesión, % que ya habían hecho la acción
                        </p>
                        <ActivatorsBySessionTable
                            data={data}
                            keys={HIGHLIGHT_KEYS}
                            catalog={data.activator_catalog}
                            labelFor={labelFor}
                        />
                    </div>

                    {/* Day behavior */}
                    <div className="rounded-xl border border-border bg-card p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                            <div>
                                <h3 className="text-sm font-semibold text-foreground">Comportamiento por día de vida</h3>
                                <p className="text-xs text-muted-foreground">
                                    Acciones ocurridas ese día (entre activos del día)
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {data.day_behavior.map((d) => (
                                    <button
                                        key={d.day}
                                        type="button"
                                        onClick={() => setSelectedLifeDay(d.day)}
                                        className={cn(
                                            'px-2.5 py-1 rounded-md text-xs font-medium transition-all',
                                            selectedLifeDay === d.day
                                                ? 'bg-primary text-primary-foreground'
                                                : 'bg-muted/50 text-muted-foreground hover:text-foreground'
                                        )}
                                    >
                                        D{d.day}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {dayBehavior && (
                            <>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                                    <MetricCard title="Elegibles" value={dayBehavior.eligible} className="p-3" />
                                    <MetricCard title="Activos" value={dayBehavior.active} className="p-3" />
                                    <MetricCard title="% activos" value={`${dayBehavior.pct_active}%`} className="p-3" />
                                    <MetricCard title="Día" value={`D${dayBehavior.day}`} className="p-3" />
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                                    {HIGHLIGHT_KEYS.map((key) => {
                                        const a = dayBehavior.activators[key];
                                        if (!a) return null;
                                        return (
                                            <div
                                                key={key}
                                                className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2"
                                            >
                                                <p className="text-[11px] text-muted-foreground truncate">
                                                    {labelFor(key, data.activator_catalog)}
                                                </p>
                                                <p className="text-sm font-semibold text-foreground">
                                                    {a.pct}%
                                                    <span className="text-xs font-normal text-muted-foreground ml-1">
                                                        ({a.users})
                                                    </span>
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {section === 'overview' && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="rounded-xl border border-border bg-card p-4">
                            <h3 className="text-sm font-semibold text-foreground mb-1">Embudo de primeras sesiones</h3>
                            <p className="text-xs text-muted-foreground mb-3">
                                Hero S1–S3 (y S5/S10): usuarios que alcanzan cada apertura
                                {isLensActive(lens) ? ' — cohorte completa; usa Patrones para el segmento filtrado' : ''}
                            </p>
                            <div className="h-56">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={sessionChart}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" />
                                        <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(215 20% 55%)" />
                                        <YAxis tick={{ fontSize: 11 }} stroke="hsl(215 20% 55%)" />
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Bar dataKey="users" radius={[4, 4, 0, 0]}>
                                            {sessionChart.map((_, i) => (
                                                <Cell
                                                    key={i}
                                                    fill={[COLORS.ocean, COLORS.teal, COLORS.amber, COLORS.emerald, COLORS.purple][i % 5]}
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="mt-3 space-y-1.5">
                                {data.session_funnel.map((s, i) => (
                                    <div key={s.session} className="flex items-center justify-between text-xs">
                                        <span className="text-muted-foreground">Sesión {s.session}</span>
                                        <span className="text-foreground font-medium">
                                            {s.users.toLocaleString()}
                                            <span className="text-muted-foreground font-normal ml-2">
                                                {s.pct_of_registered}% reg.
                                                {i > 0 && ` · ${s.pct_of_previous}% prev.`}
                                            </span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="rounded-xl border border-border bg-card p-4">
                            <h3 className="text-sm font-semibold text-foreground mb-1">Retención por día de vida</h3>
                            <p className="text-xs text-muted-foreground mb-3">
                                % activos en D1–D5, D7, D14, D21, D30
                            </p>
                            <div className="h-56">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={dayChart}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" />
                                        <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(215 20% 55%)" />
                                        <YAxis tick={{ fontSize: 11 }} stroke="hsl(215 20% 55%)" unit="%" />
                                        <Tooltip
                                            contentStyle={tooltipStyle}
                                            formatter={(value: number, _n, item) => {
                                                const row = item?.payload as { active?: number; eligible?: number };
                                                return [
                                                    `${value}% (${row?.active ?? 0}/${row?.eligible ?? 0})`,
                                                    'Activos',
                                                ];
                                            }}
                                        />
                                        <Bar dataKey="pct" fill={COLORS.teal} radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-xl border border-border bg-card p-4 space-y-4">
                        <div>
                            <h3 className="text-sm font-semibold text-foreground">Retenidos vs perdidos (48 h)</h3>
                            <p className="text-xs text-muted-foreground">
                                {data.retained_vs_churned.early_window_label}
                            </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <SegmentCard
                                title="Retenidos"
                                subtitle={data.retained_vs_churned.retained?.definition ?? '≥5 sesiones'}
                                users={data.retained_vs_churned.retained?.users ?? 0}
                                tone="good"
                            />
                            <SegmentCard
                                title="Perdidos"
                                subtitle={data.retained_vs_churned.churned?.definition ?? '≤2 sesiones'}
                                users={data.retained_vs_churned.churned?.users ?? 0}
                                tone="bad"
                            />
                        </div>
                        {data.retained_vs_churned.lift.length > 0 && (
                            <LiftTable rows={data.retained_vs_churned.lift.slice(0, 12)} />
                        )}
                    </div>

                    <div className="rounded-xl border border-border bg-card p-4">
                        <h3 className="text-sm font-semibold text-foreground mb-1">Pantallas más visitadas (sesiones 1–3)</h3>
                        <p className="text-xs text-muted-foreground mb-3">
                            Dónde navegan los nuevos usuarios al empezar
                            {isLensActive(lens) ? ' — cohorte filtrada por la lente' : ''}
                        </p>
                        {data.top_pages_sessions_1_3.length === 0 ? (
                            <p className="text-xs text-muted-foreground">Sin page_views en el periodo</p>
                        ) : (
                            <div className="space-y-2">
                                {data.top_pages_sessions_1_3.map((p) => (
                                    <div key={p.page} className="flex items-center justify-between text-sm">
                                        <span className="text-foreground font-medium">{p.page}</span>
                                        <span className="text-muted-foreground text-xs">
                                            {p.users.toLocaleString()} usuarios · {p.views.toLocaleString()} vistas
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <MainScreensPanel rows={data.patterns.by_main_screen ?? []} />

                    <button
                        type="button"
                        onClick={() => selectSection('patterns')}
                        className="w-full rounded-xl border border-primary/30 bg-primary/5 p-4 text-left hover:bg-primary/10 transition-colors"
                    >
                        <div className="flex items-center gap-2 text-primary">
                            <Lightbulb size={16} />
                            <span className="text-sm font-semibold">Ir a Patrones →</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                            Matriz acción × retención, perfiles (nivel/agua/modalidad) y oportunidades accionables
                        </p>
                    </button>
                </div>
            )}
        </div>
    );
}

function ProfileFocusCard({
    title,
    users,
    s2,
    s5,
    d7,
    extra,
}: {
    title: string;
    users: number;
    s2: number | null;
    s5: number;
    d7: number;
    extra: string;
}) {
    return (
        <div className="rounded-lg border border-border/60 bg-background/80 px-3 py-2.5">
            <p className="text-[11px] text-muted-foreground">{title} · n={users}</p>
            <div className="flex flex-wrap gap-3 mt-1 text-xs">
                {s2 != null && (
                    <span className="text-foreground">
                        S2 <strong>{s2}%</strong>
                    </span>
                )}
                <span className="text-emerald-400">
                    S5 <strong>{s5}%</strong>
                </span>
                <span className="text-foreground">
                    D7 <strong>{d7}%</strong>
                </span>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">{extra}</p>
        </div>
    );
}

function ActivatorsBySessionTable({
    data,
    keys,
    catalog,
    labelFor,
}: {
    data: EarlyLandingAnalytics;
    keys: string[];
    catalog: ActivatorCatalogItem[];
    labelFor: (key: string, catalog: ActivatorCatalogItem[] | undefined) => string;
}) {
    return (
        <table className="w-full text-xs min-w-[720px]">
            <thead>
                <tr className="text-muted-foreground border-b border-border">
                    <th className="text-left py-2 pr-2 font-medium">Activador</th>
                    {data.activation_by_session.map((s) => (
                        <th key={s.session} className="text-right py-2 px-1 font-medium">
                            S{s.session}
                            <div className="font-normal text-[10px]">{s.users_reached} users</div>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {keys.map((key) => (
                    <tr key={key} className="border-b border-border/40">
                        <td className="py-2 pr-2 text-foreground">{labelFor(key, catalog)}</td>
                        {data.activation_by_session.map((s) => {
                            const a = s.activators[key] ?? { users: 0, pct: 0 };
                            return (
                                <td key={s.session} className="text-right py-2 px-1 tabular-nums">
                                    <span className={cn(
                                        'font-medium',
                                        a.pct >= 50 ? 'text-emerald-400' : a.pct >= 20 ? 'text-amber-400' : 'text-muted-foreground'
                                    )}>
                                        {a.pct}%
                                    </span>
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function LiftTable({ rows }: { rows: Array<{ key: string; label: string; retained_pct: number; churned_pct: number; lift: number }> }) {
    return (
        <div className="overflow-x-auto">
            <p className="text-[11px] text-muted-foreground mb-2">
                Solo entra una acción si hay al menos 30 usuarios con ella y 30 sin ella.
            </p>
            <table className="w-full text-xs">
                <thead>
                    <tr className="text-muted-foreground border-b border-border">
                        <th className="text-left py-2 font-medium">Acción (lift a sesión 5)</th>
                        <th className="text-right py-2 font-medium">Con acción</th>
                        <th className="text-right py-2 font-medium">Sin acción</th>
                        <th className="text-right py-2 font-medium">Lift</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.key} className="border-b border-border/50">
                            <td className="py-2 text-foreground">{row.label}</td>
                            <td className="py-2 text-right text-emerald-400">{row.retained_pct}%</td>
                            <td className="py-2 text-right text-rose-400">{row.churned_pct}%</td>
                            <td className={cn(
                                'py-2 text-right font-semibold',
                                row.lift >= 0 ? 'text-emerald-400' : 'text-rose-400'
                            )}>
                                <span className="inline-flex items-center justify-end gap-1">
                                    {row.lift >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                    {row.lift > 0 ? '+' : ''}{row.lift} pp
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function SegmentCard({
    title,
    subtitle,
    users,
    tone,
}: {
    title: string;
    subtitle: string;
    users: number;
    tone: 'good' | 'bad';
}) {
    return (
        <div className={cn(
            'rounded-lg border p-3',
            tone === 'good' ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-rose-500/30 bg-rose-500/5'
        )}>
            <div className="flex items-center gap-2">
                <Rocket size={14} className={tone === 'good' ? 'text-emerald-400' : 'text-rose-400'} />
                <p className="text-sm font-semibold text-foreground">{title}</p>
            </div>
            <p className="text-2xl font-bold text-foreground mt-1">{users.toLocaleString()}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>
        </div>
    );
}
